import { useState, useLayoutEffect, useMemo, useRef } from 'react';
import {
  computeValuationRegression, TIME_HORIZONS, LOOKBACK_WINDOWS, DEFAULT_LOOKBACK,
} from '../utils/valuationRegression';

const HORIZONS = Object.keys(TIME_HORIZONS); // ['2Y','5Y','10Y','15Y']

/**
 * Owns all state/derivation for the "Valuation vs. Subsequent Returns" card:
 * the currently-selected backtest horizon, and the full valuation-regression
 * result for EVERY horizon (not just the selected one), so the tab bar can
 * disable/tooltip a horizon via its `isInsufficientData` up front — without
 * recomputing the backtest+OLS fit on every tab click.
 *
 * `futureFyEstimates` (the one genuinely forward-looking figure this module
 * uses — see valuationRegression.js's module docstring) is derived here,
 * not passed in directly, by filtering `metricSeriesByMetric[metric]` (the
 * backend's existing per-metric series, annual actuals + forward FY
 * estimates mixed) to points dated after "now": those are the forward FY
 * analyst-consensus estimates FMP reports; everything at/before now in that
 * same series is a historical actual and irrelevant to today's projection.
 *
 * Recomputes (all four horizons) only when the raw series' *references*
 * change (new fetch/new symbol) or `metric` changes — `price`, `shares`,
 * `epsQuarterly`, `metricSeriesByMetric`, `metric` are the useMemo
 * dependency array, so re-renders with the same arrays/metric are free.
 *
 * @param {Array<[number, number]>} price - raw price series, `[ts, price][]`
 * @param {Array<[number, number]>} shares - raw shares-outstanding series,
 *   `[ts, shares][]`, used to convert a non-EPS metric to per-share basis
 * @param {Array<[number, number]>} epsQuarterly - raw quarterly EPS actuals,
 *   `[ts, eps][]`, used for EPS's realized-NTM-EPS backtest
 * @param {{[metric: string]: Array<[number, number]>}} metricSeriesByMetric -
 *   the backend's existing per-metric series (annual actuals + forward FY
 *   estimates mixed), keyed by metric — the currently-selected `metric`'s
 *   series is used both for the annual-interpolated realized-NTM backtest
 *   (non-EPS metrics) and to derive `futureFyEstimates`
 * @param {'eps'|'revenue'|'ebitda'|'ebit'|'netIncome'|'fcf'|'ocf'|'dividends'} metric -
 *   the currently-selected comparison metric for this card
 * @param {'ntm'|'ttm'} [period='ntm'] - the currently-selected multiple
 *   vintage (see valuationRegression.js's module docstring `period` section)
 * @param {'marketCap'|'enterpriseValue'} [valueBasis='marketCap'] - the
 *   currently-selected company value basis, threaded straight through to
 *   computeValuationRegression (which switches both the multiple's numerator
 *   and, via the chart config, its P/E-vs-EV/E label off this single value).
 *   Follows the page's live top-of-page toggle — see Analysis.js.
 * @param {Array<[number, number]>} [netDebt=[]] - raw annual net-debt series,
 *   `[ts, netDebt][]`, already FX-converted onto the price axis by the
 *   backend; used only under Enterprise Value to build EV = marketCap +
 *   netDebt per historical point (realized/past-only — see
 *   realizedNetDebtPerShare). Harmlessly ignored under Market Cap.
 * @returns {{
 *   horizon: '2Y'|'5Y'|'10Y'|'15Y',
 *   setHorizon: (horizon: '2Y'|'5Y'|'10Y'|'15Y') => void,
 *   lookback: '10Y'|'15Y'|'20Y'|'25Y'|'30Y'|'All',
 *   setLookback: (lookback: '10Y'|'15Y'|'20Y'|'25Y'|'30Y'|'All') => void,
 *   resultsByHorizon: ({
 *     '2Y': object, '5Y': object, '10Y': object, '15Y': object,
 *   }|null),
 * }} `resultsByHorizon` is `null` until both `price` and `epsQuarterly` have
 *   at least one point (i.e. before the first fetch resolves) — callers use
 *   that to render a loading placeholder. A horizon a symbol lacks the
 *   history to support is still present but flagged `isInsufficientData`, so
 *   its tab renders disabled rather than vanishing.
 */
export const useValuationRegression = (price, shares, epsQuarterly, metricSeriesByMetric, metric, period = 'ntm', valueBasis = 'marketCap', netDebt = [], enabled = true) => {
  const [horizon, setHorizon] = useState('5Y');
  const [lookback, setLookback] = useState(DEFAULT_LOOKBACK);

  // The listing's own history capability check: always run against FULL
  // history (no lookbackYears applied), independent of the user's
  // evaluation-window selection. This answers a lookback-independent fact
  // about the SYMBOL itself, not about how much of its history the window
  // picker currently exposes: for each fixed holding period, could this
  // symbol/metric ever support it. Keeping this lookback-independent
  // matters for two reasons: (a) a narrow window (e.g. 15Y) makes the fixed
  // 15Y horizon mathematically self-defeating — an observation within the
  // window can never have a realized price 15 years further out, since that
  // would land in the future — so computing this check WITH the window
  // applied would spuriously flag every long-lived listing's own capability
  // as insufficient for reasons that have nothing to do with how much
  // history actually exists; (b) the default-horizon effect further down
  // keys off this value on purpose, so the user's chosen horizon tab is
  // never silently swapped out just because they changed the window.
  const fullHistoryCapability = useMemo(() => {
    // Skip the whole per-horizon sweep while the card is collapsed — `enabled`
    // is the valuation card's open state (see Analysis.js), so no regression
    // runs until the user expands it. `null` is the same shape the loading
    // state produces, and the effects below already early-return on it.
    if (!enabled) return null;
    if (!price?.length || !epsQuarterly?.length) return null;
    const nowTs = Date.now();
    const metricSeries = metricSeriesByMetric?.[metric] || [];
    const futureFyEstimates = metricSeries.filter(([ts]) => ts > nowTs);
    const inputs = { price, shares, netDebt, metric, epsQuarterly, metricSeries, futureFyEstimates, nowTs, period, valueBasis };
    // Per-FIXED-horizon sufficiency against FULL history — lookback-independent
    // by design (see the note above), so it answers "could this symbol/metric
    // ever support this holding period", not "does the current window expose
    // enough of it". Used both to decide the default tab and, in the reset
    // effect below, to decide whether the user's current tab is still a valid
    // choice worth preserving across a symbol/metric change.
    const fixedSufficient = HORIZONS.reduce((acc, h) => {
      acc[h] = !computeValuationRegression({ ...inputs, horizon: h }).isInsufficientData;
      return acc;
    }, {});
    return { fixedSufficient };
  }, [enabled, price, shares, netDebt, epsQuarterly, metricSeriesByMetric, metric, period, valueBasis]);

  // The displayed per-horizon results: the 4 fixed tabs, ALL computed WITH
  // the user's selected evaluation window applied — this is what the window
  // picker is actually for, limiting which observations feed every tab's
  // chart/fit alike. A symbol lacking the history for a given horizon still
  // gets a result here (flagged `isInsufficientData`), so its tab renders
  // disabled rather than vanishing.
  const resultsByHorizon = useMemo(() => {
    // Gated on the card's open state — see fullHistoryCapability above. The
    // consuming card renders a loading placeholder for a null result.
    if (!enabled) return null;
    if (!price?.length || !epsQuarterly?.length) return null;
    const nowTs = Date.now();
    const metricSeries = metricSeriesByMetric?.[metric] || [];
    const futureFyEstimates = metricSeries.filter(([ts]) => ts > nowTs);
    const lookbackYears = LOOKBACK_WINDOWS[lookback] ?? null;
    const inputs = { price, shares, netDebt, metric, epsQuarterly, metricSeries, futureFyEstimates, lookbackYears, nowTs, period, valueBasis };

    return HORIZONS.reduce((acc, h) => {
      acc[h] = computeValuationRegression({ ...inputs, horizon: h });
      return acc;
    }, {});
  }, [enabled, price, shares, netDebt, epsQuarterly, metricSeriesByMetric, metric, lookback, period, valueBasis]);

  // Whenever the underlying data changes (new fetch, new symbol, or a new
  // metric selection) — i.e. whenever `fullHistoryCapability` becomes a new
  // object — the user's currently-selected horizon may no longer be a valid
  // tab: the active fixed horizon can become insufficient for the new
  // symbol/metric. Only in that case do we snap to the default (5Y, the
  // product default, when supported; else the longest fixed tab that IS
  // sufficient; else 15Y anyway — its own "not enough trading history"
  // message covers a symbol too young for even a 2Y backtest).
  //
  // Crucially, if the current horizon is STILL a valid, sufficient tab under
  // the new capability, we leave it untouched. This is what keeps a metric
  // switch (e.g. OCF → FCF) from yanking a deliberately-chosen shorter
  // holding period back up to 15Y — which, paired with a narrow evaluation
  // window, would otherwise produce the spurious "the NY window can't contain
  // a realized 15Y-later return" message for a choice the user never made.
  // Switching the evaluation window (`lookback`) is a separate concern,
  // handled by its own effect further down: this (fullHistoryCapability)
  // effect never reacts to a `lookback`-only change (the capability check
  // above is deliberately lookback-independent), but a dedicated
  // lookback-driven effect auto-switches the active tab specifically when
  // narrowing/widening the window makes IT become insufficient — see that
  // effect's docstring for why it's kept separate from this one.
  //
  // useLayoutEffect, not useEffect: `horizon` and `fullHistoryCapability`
  // are two independent pieces of state, so a switch that makes the active
  // tab insufficient can otherwise paint one frame with a stale selection
  // before the correction lands. useLayoutEffect corrects `horizon`
  // synchronously before the browser paints, so that mismatched frame is
  // never actually shown.
  useLayoutEffect(() => {
    if (!fullHistoryCapability) return;
    const { fixedSufficient } = fullHistoryCapability;
    // Is the user's current tab still a valid, sufficient choice under the new
    // symbol/metric? Checked against FULL-history capability (not the windowed
    // `resultsByHorizon`), so a narrow evaluation window never makes this snap
    // the tab — that stays the card's own per-horizon message, not a silent
    // swap.
    if (fixedSufficient?.[horizon] === true) return;
    // Default holding period is 5Y (the product default). Prefer it when the
    // symbol/metric supports it; otherwise fall back to the longest fixed tab
    // that IS sufficient (a very short listing may not have 5 years of usable
    // backtest history but might support 2Y), and finally to 15Y — whose own
    // "not enough trading history" message covers a symbol too young for even
    // a 2Y backtest.
    if (fixedSufficient?.['5Y']) {
      setHorizon('5Y');
    } else if (fixedSufficient?.['2Y']) {
      setHorizon('2Y');
    } else {
      setHorizon('15Y');
    }
  }, [fullHistoryCapability, horizon]);

  // Product override of the "never swap the user's tab on a window change"
  // rule documented above, for exactly one case: if narrowing/widening the
  // evaluation window makes the CURRENTLY ACTIVE horizon tab itself become
  // insufficient (e.g. a 15Y window can never contain a realized 15Y-later
  // price, so the 15Y tab goes insufficient the moment the window narrows
  // to 15Y), auto-switch to the largest still-sufficient tab so the user
  // keeps seeing a chart instead of a "not enough history" placeholder.
  //
  // Kept as its own effect, deliberately separate from the
  // fullHistoryCapability effect above:
  //  - That effect answers "is the tab still valid for this SYMBOL/METRIC",
  //    checked against FULL history, and its own fallback order (5Y, then
  //    2Y, then 15Y) is the *default-selection* preference — unrelated to,
  //    and must not be conflated with, this effect's "biggest tab that
  //    still works under the new window" fallback.
  //  - This effect answers "is the tab still valid for this WINDOW",
  //    checked against the windowed `resultsByHorizon`, and only ever
  //    fires off an actual `lookback` change — never off `horizon`
  //    changing by itself (a disabled/insufficient tab can't be clicked in
  //    the first place, so there's nothing to "fight" there, but this
  //    guards against it regardless).
  //
  // To never race the other effect for the same `setHorizon` call in the
  // same render (e.g. a symbol switch that happens to land in the same
  // commit as a `lookback` change), this effect additionally only ever
  // acts when the active tab is STILL valid per `fullHistoryCapability`
  // (the lookback-independent, symbol/metric-driven check) — i.e. when the
  // insufficiency it just observed in the windowed `resultsByHorizon` can
  // only be explained by the window itself, not by the underlying
  // symbol/metric data. If the tab is ALSO invalid at the full-history
  // level, that's squarely the other effect's job and this one defers to
  // it entirely. Deliberately checked by VALUE (`fixedSufficient`), not by
  // comparing `fullHistoryCapability`'s object
  // identity — that reference can change across renders for reasons
  // unrelated to an actual symbol/metric change (e.g. a parent re-render
  // handing down a fresh-but-equal array), so identity comparison would be
  // an unreliable proxy for "did the capability itself actually change".
  const prevLookbackRef = useRef(lookback);
  useLayoutEffect(() => {
    const lookbackChanged = prevLookbackRef.current !== lookback;
    prevLookbackRef.current = lookback;

    // Only a standalone lookback change is this effect's concern; and
    // `resultsByHorizon` being briefly null (still loading) has nothing to
    // switch away from yet.
    if (!lookbackChanged || !resultsByHorizon) return;

    const activeResult = resultsByHorizon[horizon];
    // Nothing to do if the active tab is still sufficient (the common
    // case — most window changes don't affect the active horizon at all)
    // or isn't resolvable yet.
    if (!activeResult?.isInsufficientData) return;

    // Defer to the other effect if the active tab is ALSO invalid at the
    // full-history level — this effect only ever handles a WINDOW-caused
    // insufficiency.
    const capabilityStillValid = fullHistoryCapability?.fixedSufficient?.[horizon] === true;
    if (!capabilityStillValid) return;

    // Biggest to smallest of the fixed tabs: keep the user on the longest
    // holding period that still works under the newly-selected window.
    const fallbackOrder = ['15Y', '10Y', '5Y', '2Y'];
    const nextHorizon = fallbackOrder.find(
      (h) => resultsByHorizon[h] && !resultsByHorizon[h].isInsufficientData
    );
    // If literally nothing is sufficient under the new window (edge case),
    // leave `horizon` untouched so the existing insufficient-data message
    // still renders rather than swapping to a value that's equally broken.
    if (nextHorizon && nextHorizon !== horizon) {
      setHorizon(nextHorizon);
    }
  }, [lookback, resultsByHorizon, fullHistoryCapability, horizon]);

  return { horizon, setHorizon, lookback, setLookback, resultsByHorizon };
};

export default useValuationRegression;
